/**
 * @file FontCatalogRow.tsx
 * @description Строка каталога шрифтов (phase19.16 Р1, Р1-7; uiux-design-r1
 * §1): «◉ Lora · С засечками · 4 начертания · сейчас» и образец. Высота строки
 * ~72px.
 *
 * Строка — контейнер: выбор шрифта — радиокнопка на всю строку, справа —
 * необязательное меню «⋯» (только у своих шрифтов магазина: догрузить
 * начертание, переименовать, удалить). Кнопка в кнопке недопустима, поэтому
 * меню — соседний элемент, а не часть радиокнопки.
 */

import type { ReactNode } from "react";

import { FontSampleText } from "./FontSampleText";
import { cn } from "@/lib/utils";

interface Props {
  fontKey: string;
  family: string;
  /** Подпись справа от названия: «С засечками · 4 начертания · сейчас». */
  meta: string;
  /** Пометки рядом с названием («ваш», «без русских букв ⚠»). */
  badges?: ReactNode;
  sample: string;
  selected: boolean;
  onSelect: () => void;
  /** Меню «⋯» строки; нет — строка без меню (шрифты платформы). */
  menu?: ReactNode;
  /** Строка в списке `role="list"` (свои шрифты: радиокнопка и меню рядом). */
  listItem?: boolean;
}

export function FontCatalogRow({ fontKey, family, meta, badges, sample, selected, onSelect, menu, listItem = false }: Props) {
  return (
    <div
      role={listItem ? "listitem" : undefined}
      className={cn(
        "flex min-h-[4.5rem] w-full items-center gap-2 border-b border-[var(--border)] pr-2 last:border-b-0",
        selected ? "bg-accent" : "hover:bg-surface-low",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        className="flex min-h-[4.5rem] min-w-0 flex-1 items-start gap-3 py-3 pl-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span
          aria-hidden
          className={cn(
            "mt-1 inline-flex size-4 shrink-0 items-center justify-center rounded-full [box-shadow:inset_0_0_0_1.5px_var(--outline-variant)]",
            selected && "[box-shadow:inset_0_0_0_5px_var(--primary)]",
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="type-body font-medium text-on-surface">{family}</span>
              {badges}
            </span>
            <span className="type-caption text-on-surface-variant">{meta}</span>
          </span>
          <FontSampleText fontKey={fontKey} text={sample} size={18} />
        </span>
      </button>
      {menu ? <div className="shrink-0">{menu}</div> : null}
    </div>
  );
}
